/**
 * @fileoverview Letterhead Variants Manager
 *
 * Lives under Settings → Branding → Variants. Lists every letterhead
 * variant for the org and provides CRUD + tagging + reordering. Variants
 * are the unit of selection at render time: tags decide which variants
 * apply to a given job; the per-variant page rules decide which pages
 * each one shows up on.
 *
 * Letterhead asset upload (PDF crop -> 4 zone images) reuses the existing
 * client-side extraction pipeline. New variants start blank — the user
 * uploads a source document via the same drop zone the org-level profile
 * uses, but it writes to a `letterheadVariants` row instead.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import type { FunctionReturnType } from "convex/server";
import { api } from "~/convex/_generated/api";
import type { Id } from "~/convex/_generated/dataModel";
import { Button, Input } from "@/ui/primitives";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/ui/composed/overlays/alert-dialog";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Star, Trash2, Plus } from "lucide-react";
import { cn } from "@/utils/misc";
import { extractErrorMessage } from "@/utils/errors";
import {
  resolveDocumentPadding,
  variantPaddingTiers,
  type LayoutMarginEntry,
  type PageRule,
} from "@/utils/branding/resolve-letterhead";
import { buildVariantFragments } from "@/utils/branding/letterheadFragments";
import {
  derivePageLayoutCombos,
  PAGE_FOR_CLASS,
  type LayoutCombo,
} from "@/utils/branding/layoutCombos";
import { useMarginUnit } from "@/utils/branding/marginUnits";
import {
  LayoutMarginsEditor,
  LayoutMarginPreviews,
} from "./LayoutMarginsEditor";

const PAGE_RULE_LABELS: Record<PageRule, string> = {
  all: "All pages",
  firstOnly: "First only",
  notFirst: "Not first",
  oddOnly: "Odd only",
  evenOnly: "Even only",
  none: "Never (off)",
};

const ZONE_LABELS: Record<string, string> = {
  header: "Header",
  footer: "Footer",
  leftSide: "Left side",
  rightSide: "Right side",
};

/**
 * Row shapes as returned by the branding queries — a variant / asset doc
 * with its storage IDs resolved to URLs (so it's renderer-ready).
 */
type VariantRow = FunctionReturnType<
  typeof api.settings.branding.variantQueries.listVariantsForOrg
>[number];
type AssetRow = FunctionReturnType<
  typeof api.settings.branding.assetQueries.listAssetsForOrg
>[number];

/**
 * Build the picker label for a letterhead asset from its *live* bundle
 * name rather than the stored `name` field.
 *
 * `name` is frozen at creation time as "<bundleName> — <zone>"; renaming a
 * bundle only updates `bundleName`, so `name` goes stale and the picker
 * would show the original document name instead of the user's chosen one.
 * Mirror groupAssetsIntoBundles' fallback chain so both surfaces agree.
 */
function assetPickerLabel(a: AssetRow): string {
  const bundle =
    a.bundleName ||
    a.sourceDocumentName ||
    a.name.replace(/\s*—\s*(Header|Footer|Left side|Right side)\s*$/i, "") ||
    "Untitled letterhead";
  const zone = ZONE_LABELS[a.zoneKind];
  return zone ? `${bundle} — ${zone}` : bundle;
}

export function LetterheadVariantsManager({
  orgId,
  canEdit,
  showDivider = false,
}: {
  orgId: Id<"organizations">;
  canEdit: boolean;
  showDivider?: boolean;
}) {
  const { data: variants } = useQuery({
    ...convexQuery(api.settings.branding.variantQueries.listVariantsForOrg, {
      orgId,
    }),
    enabled: !!orgId,
  });

  // Asset library for the zone pickers inside each variant card.
  const { data: assets } = useQuery({
    ...convexQuery(api.settings.branding.assetQueries.listAssetsForOrg, {
      orgId,
    }),
    enabled: !!orgId,
  });

  const { mutateAsync: createVariant } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.branding.variantMutations.createVariant,
    ),
  });
  const { mutateAsync: deleteVariant } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.branding.variantMutations.deleteVariant,
    ),
  });
  const { mutateAsync: toggleFavouriteVariant } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.branding.variantMutations.toggleFavouriteVariant,
    ),
  });
  const { mutateAsync: setFirmDefaultLetterheadVariant } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.branding.mutations.setFirmDefaultLetterheadVariant,
    ),
  });

  // Firm-default pointer (MOR-953) — a variant can be the org-wide default
  // so multi-page letterheads (first page + rest of pages) apply everywhere.
  const { data: brandingProfile } = useQuery({
    ...convexQuery(api.settings.branding.queries.getBrandingProfile, {
      orgId,
    }),
    enabled: !!orgId,
  });
  const firmDefaultVariantId =
    brandingProfile?.firmDefaultLetterheadVariantId ?? null;

  const [newVariantName, setNewVariantName] = useState("");

  const handleCreate = async () => {
    if (!newVariantName.trim()) {
      toast.error("Give the variant a name first");
      return;
    }
    try {
      await createVariant({ orgId, name: newVariantName.trim() });
      setNewVariantName("");
      toast.success("Variant created");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to create variant"));
    }
  };

  // Common handlers used by both grid + table renderers below
  const handleToggleFavourite = async (v: VariantRow) => {
    try {
      await toggleFavouriteVariant({ variantId: v._id, orgId });
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to update favourite"));
    }
  };
  const handleDelete = async (v: VariantRow) => {
    try {
      await deleteVariant({ variantId: v._id, orgId });
      toast.success(`Deleted "${v.name}"`);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to delete"));
    }
  };
  const handleToggleFirmDefault = async (v: VariantRow) => {
    const isDefault = firmDefaultVariantId === v._id;
    try {
      await setFirmDefaultLetterheadVariant({
        orgId,
        variantId: isDefault ? null : v._id,
      });
      toast.success(
        isDefault ? "Cleared firm default" : `Set "${v.name}" as firm default`,
      );
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to set firm default"));
    }
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-4 px-6 py-4",
        showDivider && "border-t border-border/50",
      )}
    >
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <h3 className="font-sans text-sm font-medium text-primary/70">
            Letterhead Variants
          </h3>
          <p className="mt-0.5 max-w-2xl text-xs text-primary/40">
            Variants are presets that can be applied to each Letterhead zone
            (i.e. header / footer / sides). Use variants to add one or more
            rules (e.g. apply to all pages, first only, rest of pages, etc.) and
            which letterhead zone to use. Star a variant to surface it at the
            top of the per-document picker.
          </p>
        </div>
      </div>

      {canEdit && (
        <div className="flex items-center gap-2 rounded-lg border border-dashed border-border bg-secondary/30 p-3">
          <Input
            value={newVariantName}
            onChange={(e) => setNewVariantName(e.target.value)}
            placeholder='New variant name (e.g. "Header for subsequent pages")'
            className="h-8 text-xs"
            onKeyDown={(e) => {
              if (e.key === "Enter") void handleCreate();
            }}
          />
          <Button size="sm" onClick={handleCreate} className="h-8 text-xs">
            <Plus className="mr-1 size-3" /> Add variant
          </Button>
        </div>
      )}

      {variants?.length === 0 && (
        <div className="rounded-lg border border-border bg-background p-6 text-center text-sm text-primary/50">
          No variants yet. Variants are optional — most firms only need one or
          two for special cases. Pick a letterhead per document via the picker
          on the day-one page.
        </div>
      )}

      <div className="flex flex-col gap-3">
        {variants?.map((v) => (
          <VariantCard
            key={v._id}
            variant={v}
            orgId={orgId}
            canEdit={canEdit}
            assets={assets ?? []}
            isFirmDefault={firmDefaultVariantId === v._id}
            onToggleFirmDefault={() => void handleToggleFirmDefault(v)}
            onToggleFavourite={() => void handleToggleFavourite(v)}
            onDelete={() => void handleDelete(v)}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// VariantCard — one row in the variants list
// ---------------------------------------------------------------------------

function VariantCard({
  variant,
  orgId,
  canEdit,
  assets,
  isFirmDefault,
  onToggleFirmDefault,
  onToggleFavourite,
  onDelete,
}: {
  variant: VariantRow;
  orgId: Id<"organizations">;
  canEdit: boolean;
  assets: AssetRow[];
  isFirmDefault: boolean;
  onToggleFirmDefault: () => void | Promise<void>;
  onToggleFavourite: () => void | Promise<void>;
  onDelete: () => void | Promise<void>;
}) {
  const { mutateAsync: updateVariant } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.branding.variantMutations.updateVariant,
    ),
  });
  const { mutateAsync: setVariantZoneLayers } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.branding.variantMutations.setVariantZoneLayers,
    ),
  });

  // Each zone is now a list of layers. Replace the whole array on every
  // edit — simpler than a per-layer mutation, and the lists are small
  // (typically 1-2 entries).
  const persistLayers = async (
    zone: "header" | "footer" | "leftSide" | "rightSide",
    layers: Array<{ assetId: Id<"letterheadAssets">; pageRule: PageRule }>,
  ) => {
    try {
      await setVariantZoneLayers({
        variantId: variant._id,
        orgId,
        zone,
        layers,
      });
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to save"));
    }
  };

  const handleUpdate = async (patch: Record<string, unknown>) => {
    try {
      await updateVariant({
        variantId: variant._id,
        orgId,
        ...patch,
      });
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to save"));
    }
  };

  const [collapsed, setCollapsed] = useState(false);

  // Margins per layout: the combinations this variant produces, and what a
  // row inherits when it sets nothing (firm defaults + the variant's own
  // uniform padding, resolved the same way the renderers resolve them).
  const { data: brandingProfile } = useQuery({
    ...convexQuery(api.settings.branding.queries.getBrandingProfile, { orgId }),
    enabled: !!orgId,
  });
  const [marginUnit] = useMarginUnit();
  const assetNames = useMemo(
    () => Object.fromEntries(assets.map((a) => [a._id, a.name])),
    [assets],
  );
  const variantFragments = useMemo(
    () => buildVariantFragments(variant),
    [variant],
  );
  const layoutCombos = useMemo(
    () => derivePageLayoutCombos(variantFragments, assetNames),
    [variantFragments, assetNames],
  );
  // The variant's own padding, then the body column its page-fit
  // letterhead measured from the source page (MOR-1432) — resolved per page
  // class, because a `firstOnly` header and a `notFirst` header can each
  // carry their own body column and the renderers resolve it per page.
  const inheritedPadding = useMemo(() => {
    const forClass = (pageClass: LayoutCombo["pageClass"]) =>
      resolveDocumentPadding(
        brandingProfile ?? null,
        null,
        null,
        variantPaddingTiers(variantFragments, PAGE_FOR_CLASS[pageClass]),
      );
    return {
      first: forClass("first"),
      other: forClass("other"),
      even: forClass("even"),
    };
  }, [brandingProfile, variantFragments]);
  const inheritedFor = useCallback(
    (combo: LayoutCombo) => inheritedPadding[combo.pageClass],
    [inheritedPadding],
  );
  const layoutMarginEntries: LayoutMarginEntry[] = variant.layoutMargins ?? [];

  const totalRules =
    readLayers(variant, "header").length +
    readLayers(variant, "footer").length +
    readLayers(variant, "leftSide").length +
    readLayers(variant, "rightSide").length;

  // Local draft for the name input. Firing updateVariant on every
  // keystroke causes the convex query to refetch and clobber the input's
  // value/cursor mid-type. Keep the draft in local state and only persist
  // on blur / Enter / Escape.
  const [nameDraft, setNameDraft] = useState(variant.name);
  const nameFocusedRef = useRef(false);
  useEffect(() => {
    // Only sync from server when the user isn't actively editing — avoids
    // overwriting in-progress typing when another tab or background patch
    // bumps the row.
    if (!nameFocusedRef.current) setNameDraft(variant.name);
  }, [variant.name]);
  const persistName = () => {
    nameFocusedRef.current = false;
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      // Don't allow an empty name — revert.
      setNameDraft(variant.name);
      return;
    }
    if (trimmed === variant.name) return;
    void handleUpdate({ name: trimmed });
  };

  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-1 items-start gap-2">
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            className="mt-1 rounded p-0.5 text-primary/40 transition hover:bg-secondary hover:text-primary/70"
            title={collapsed ? "Expand" : "Collapse"}
            aria-label={collapsed ? "Expand variant" : "Collapse variant"}
          >
            {collapsed ? (
              <ChevronRight className="size-4" />
            ) : (
              <ChevronDown className="size-4" />
            )}
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Input
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onFocus={() => {
                  nameFocusedRef.current = true;
                }}
                onBlur={persistName}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.currentTarget.blur();
                  } else if (e.key === "Escape") {
                    setNameDraft(variant.name);
                    e.currentTarget.blur();
                  }
                }}
                className="h-7 max-w-xs text-sm font-medium"
                disabled={!canEdit}
              />
              {variant.isFavourite && (
                <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
                  <Star className="size-3" />
                  Favourite
                </span>
              )}
              {isFirmDefault && (
                <span className="inline-flex shrink-0 items-center gap-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300">
                  Firm default
                </span>
              )}
            </div>
            <div className="mt-1 flex items-center gap-2">
              <p className="text-xs text-primary/50">
                Custom letterhead preset — picked per document.
              </p>
              {collapsed && totalRules > 0 && (
                <span className="inline-flex shrink-0 items-center rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary/60">
                  {totalRules} {totalRules === 1 ? "rule" : "rules"} applied
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {canEdit && (
            <>
              <button
                type="button"
                onClick={() => void onToggleFirmDefault()}
                className={cn(
                  "rounded px-2 py-1 text-[11px] transition",
                  isFirmDefault
                    ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300"
                    : "border border-border bg-background text-primary/60 hover:bg-secondary",
                )}
                title={
                  isFirmDefault
                    ? "Clear firm default — documents will fall back to the default letterhead bundle (if any)"
                    : "Make this variant the firm default — used by every document with no override (supports first-page + subsequent-page rules)"
                }
              >
                {isFirmDefault ? "Clear default" : "Set as firm default"}
              </button>
              <button
                type="button"
                onClick={() => void onToggleFavourite()}
                className={cn(
                  "rounded p-1 transition hover:bg-secondary",
                  variant.isFavourite ? "text-amber-500" : "text-primary/40",
                )}
                title={
                  variant.isFavourite
                    ? "Remove from favourites"
                    : "Favourite — surfaces at the top of the per-document picker"
                }
              >
                <Star className="size-4" />
              </button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    type="button"
                    className="rounded p-1 text-primary/40 transition hover:bg-destructive/10 hover:text-destructive"
                    title="Delete variant"
                  >
                    <Trash2 className="size-4" />
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this variant?</AlertDialogTitle>
                    <AlertDialogDescription>
                      &quot;{variant.name}&quot; and its letterhead assets will
                      be permanently deleted. Documents currently set to use
                      this variant via Manual override will fall back to Auto
                      matching.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => void onDelete()}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </div>
      </div>

      {!collapsed && (
        <>
          {/* Letterhead zones — each row picks an asset from the library above
           *  and sets its own page rule. Variants are pure compositions; new
           *  letterhead artwork is uploaded in the Letterheads section. */}
          <div className="mt-4 border-t border-border pt-4">
            <div className="mb-3 flex items-center text-[10px] font-medium uppercase tracking-wide text-primary/40">
              Letterhead zones
            </div>

            <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
              <div className="flex min-w-0 flex-1 flex-col gap-3">
                <ZoneLayers
                  label="Header"
                  zone="header"
                  canEdit={canEdit}
                  assetsForZone={assets.filter((a) => a.zoneKind === "header")}
                  layers={readLayers(variant, "header")}
                  onChange={(layers) => void persistLayers("header", layers)}
                />
                <ZoneLayers
                  label="Footer"
                  zone="footer"
                  canEdit={canEdit}
                  assetsForZone={assets.filter((a) => a.zoneKind === "footer")}
                  layers={readLayers(variant, "footer")}
                  onChange={(layers) => void persistLayers("footer", layers)}
                />
                <ZoneLayers
                  label="Left side"
                  zone="leftSide"
                  canEdit={canEdit}
                  assetsForZone={assets.filter(
                    (a) => a.zoneKind === "leftSide",
                  )}
                  layers={readLayers(variant, "leftSide")}
                  onChange={(layers) => void persistLayers("leftSide", layers)}
                />
                <ZoneLayers
                  label="Right side"
                  zone="rightSide"
                  canEdit={canEdit}
                  assetsForZone={assets.filter(
                    (a) => a.zoneKind === "rightSide",
                  )}
                  layers={readLayers(variant, "rightSide")}
                  onChange={(layers) => void persistLayers("rightSide", layers)}
                />
              </div>

              {/* Live composition preview — one editable sample page per
               *  distinct layout this variant's rules produce. Margins for each
               *  layout are dragged directly on the page, like the crop editor. */}
              {variantFragments.length > 0 && (
                <div className="shrink-0">
                  <LayoutMarginPreviews
                    combos={layoutCombos}
                    entries={layoutMarginEntries}
                    inherited={inheritedFor}
                    unit={marginUnit}
                    canEdit={canEdit}
                    fragments={variantFragments}
                    onSave={(layoutMargins) => handleUpdate({ layoutMargins })}
                  />
                </div>
              )}
            </div>
          </div>

          <LayoutMarginsEditor
            combos={layoutCombos}
            entries={layoutMarginEntries}
            inherited={inheritedFor}
            unit={marginUnit}
            canEdit={canEdit}
            onSave={(layoutMargins) => handleUpdate({ layoutMargins })}
          />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Zone layers — each zone holds an ordered list of (asset, pageRule) pairs.
//
// The renderer walks the list per page and uses the first layer whose
// rule includes that page. This lets the user stack e.g. "first page =
// Sydney header" and "rest of pages = Morgan header" inside one variant.
// ---------------------------------------------------------------------------

type Layer = { assetId: Id<"letterheadAssets">; pageRule: PageRule };

/**
 * Page rules that can appear in a layer dropdown. `none` is not a valid
 * layer rule — if the user wants the zone off, they remove the rule
 * entirely. Order here defines the order shown in the dropdown.
 */
const LAYER_PAGE_RULES: PageRule[] = [
  "all",
  "firstOnly",
  "notFirst",
  "oddOnly",
  "evenOnly",
];

/**
 * For the page-rule dropdown at row `currentIdx`, return the set of
 * rules that would create a conflict (and should be disabled). A rule
 * conflicts when:
 *   - it's already used by another row in the same zone, OR
 *   - it's "all" and any other row exists (covers everything → no room
 *     for siblings).
 * The dropdown still SHOWS the current row's own rule so it can render
 * its selected value.
 */
function disabledPageRulesForRow(
  rules: Layer[],
  currentIdx: number,
): Set<PageRule> {
  const disabled = new Set<PageRule>();
  const usedByOthers = rules
    .filter((_, i) => i !== currentIdx)
    .map((l) => l.pageRule);
  for (const used of usedByOthers) disabled.add(used);
  if (usedByOthers.length > 0) disabled.add("all");
  return disabled;
}

/**
 * Can the user add another rule to this zone? No when "all" is already
 * used (covers everything) or every selectable rule is already taken.
 */
function canAddRule(rules: Layer[]): boolean {
  if (rules.some((r) => r.pageRule === "all")) return false;
  const used = new Set(rules.map((r) => r.pageRule));
  return LAYER_PAGE_RULES.some((r) => !used.has(r) && r !== "all");
}

/**
 * Pick a sensible default rule for a brand-new layer — prefers the
 * first unused rule. Called when the user clicks "+ Add rule".
 */
function nextDefaultRule(rules: Layer[]): PageRule {
  const used = new Set(rules.map((r) => r.pageRule));
  for (const r of LAYER_PAGE_RULES) {
    if (!used.has(r)) return r;
  }
  return "all";
}

/**
 * Read the current layers for a zone, falling back to the legacy single
 * asset + single page rule pair when the variant hasn't been migrated to
 * the new layered model yet.
 */
function readLayers(
  variant: VariantRow,
  zone: "header" | "footer" | "leftSide" | "rightSide",
): Layer[] {
  const layersField = (
    {
      header: "headerLayers",
      footer: "footerLayers",
      leftSide: "leftSideLayers",
      rightSide: "rightSideLayers",
    } as const
  )[zone];
  const layers = variant[layersField];
  if (layers && layers.length > 0) return layers;

  // Legacy fallback: single asset + single page rule.
  const legacyAssetField = (
    {
      header: "headerAssetId",
      footer: "footerAssetId",
      leftSide: "leftSideAssetId",
      rightSide: "rightSideAssetId",
    } as const
  )[zone];
  const legacyRuleField = (
    {
      header: "headerPageRule",
      footer: "footerPageRule",
      leftSide: "leftSidePageRule",
      rightSide: "rightSidePageRule",
    } as const
  )[zone];
  const assetId = variant[legacyAssetField];
  if (!assetId) return [];
  return [{ assetId, pageRule: variant[legacyRuleField] ?? "all" }];
}

function ZoneLayers({
  label,
  zone: _zone,
  canEdit,
  assetsForZone,
  layers,
  onChange,
}: {
  label: string;
  zone: "header" | "footer" | "leftSide" | "rightSide";
  canEdit: boolean;
  assetsForZone: AssetRow[];
  layers: Layer[];
  onChange: (next: Layer[]) => void;
}) {
  const updateLayer = (idx: number, patch: Partial<Layer>) => {
    const next = layers.map((l, i) => (i === idx ? { ...l, ...patch } : l));
    onChange(next);
  };
  const removeLayer = (idx: number) => {
    onChange(layers.filter((_, i) => i !== idx));
  };
  const addLayer = () => {
    // Default new layer: first available asset + next unused page rule.
    // (User adjusts immediately afterwards.)
    const first = assetsForZone[0];
    if (!first) return;
    onChange([
      ...layers,
      { assetId: first._id, pageRule: nextDefaultRule(layers) },
    ]);
  };

  const addDisabled = assetsForZone.length === 0 || !canAddRule(layers);
  const addTitle = (() => {
    if (assetsForZone.length === 0)
      return `No ${label.toLowerCase()} assets uploaded yet`;
    if (layers.some((r) => r.pageRule === "all"))
      return `"All pages" already covers every page — no more rules needed`;
    if (!canAddRule(layers)) return `Every page range is already in use`;
    return "Add another rule for this zone";
  })();

  return (
    <div className="rounded border border-border bg-secondary/20 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-medium text-primary/70">{label}</span>
        {canEdit && (
          <button
            type="button"
            onClick={addLayer}
            disabled={addDisabled}
            className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-primary/50 transition hover:bg-secondary disabled:opacity-40"
            title={addTitle}
          >
            <Plus className="size-3" />
            Add rule
          </button>
        )}
      </div>

      {layers.length === 0 ? (
        <p className="rounded border border-dashed border-border bg-background px-3 py-2 text-[11px] text-primary/40">
          {assetsForZone.length === 0
            ? `Upload a ${label.toLowerCase()} above first, then add it here.`
            : `No ${label.toLowerCase()} on any page. Click "Add rule" to pick one.`}
        </p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {layers.map((layer, idx) => (
            <LayerRow
              key={idx}
              layer={layer}
              canEdit={canEdit}
              assetsForZone={assetsForZone}
              disabledPageRules={disabledPageRulesForRow(layers, idx)}
              onChange={(patch) => updateLayer(idx, patch)}
              onRemove={() => removeLayer(idx)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function LayerRow({
  layer,
  canEdit,
  assetsForZone,
  disabledPageRules,
  onChange,
  onRemove,
}: {
  layer: Layer;
  canEdit: boolean;
  assetsForZone: AssetRow[];
  disabledPageRules: Set<PageRule>;
  onChange: (patch: Partial<Layer>) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      {/* Page rule first — read left-to-right as "on these pages, use this letterhead". */}
      <Select
        value={layer.pageRule}
        onValueChange={(v) => onChange({ pageRule: v as PageRule })}
        disabled={!canEdit}
      >
        <SelectTrigger className="h-7 w-[160px] text-[11px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {LAYER_PAGE_RULES.map((rule) => (
            <SelectItem
              key={rule}
              value={rule}
              className="text-xs"
              disabled={disabledPageRules.has(rule) && rule !== layer.pageRule}
            >
              {PAGE_RULE_LABELS[rule]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <span className="text-[11px] text-primary/40">use</span>

      <Select
        value={layer.assetId}
        onValueChange={(v) =>
          onChange({ assetId: v as Id<"letterheadAssets"> })
        }
        disabled={!canEdit}
      >
        <SelectTrigger className="h-7 min-w-[180px] flex-1 text-[11px]">
          <SelectValue placeholder="Pick a letterhead…" />
        </SelectTrigger>
        <SelectContent>
          {assetsForZone.length === 0 ? (
            <div className="px-2 py-1.5 text-[11px] text-primary/40">
              No assets uploaded for this zone
            </div>
          ) : (
            assetsForZone.map((a) => (
              <SelectItem key={a._id} value={a._id} className="text-xs">
                {assetPickerLabel(a)}
                {a.isDefault && <span className="ml-1 text-amber-500">★</span>}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>

      {canEdit && (
        <button
          type="button"
          onClick={onRemove}
          className="rounded p-1 text-primary/40 transition hover:bg-destructive/10 hover:text-destructive"
          title="Remove this rule"
        >
          <Trash2 className="size-3" />
        </button>
      )}
    </div>
  );
}
